/**
 * English — the source of truth.
 *
 * Every other locale is a translation of exactly these keys. A key that exists
 * here and nowhere else falls back to English at runtime, which is the correct
 * failure: untranslated, never broken.
 */

export default {
  code: 'en',
  intl: 'en',
  name: 'English',
  endonym: 'English',
  reviewed: true,
  strings: {
    /* ── chrome ─────────────────────────────────────────────────────── */
    'app.tagline': 'Reads your heartbeat from a camera — and says so honestly when it cannot.',
    'app.disclaimer':
      'Working prototype. Not a medical device, not validated, not a diagnosis.',
    'app.skip': 'Skip to the main content',
    'app.language': 'Language',
    'app.install': 'Install Ori on this device',
    'app.update.title': 'A new version of Ori is ready',
    'app.update.body':
      'It will be used the next time you open the app, or you can switch now. Nothing you have saved is affected.',
    'app.update.action': 'Update now',
    'app.queued': '{count} readings waiting to send',
    'app.queued.one': '1 reading waiting to send',

    'nav.start': 'What this is',
    'nav.scan': 'Take a reading',
    'nav.ori': 'Your Ori',
    'nav.record': 'Your record',
    'nav.consent': 'What you share',
    'nav.care': 'Where to go next',
    'nav.pledge': 'The Skin Pledge',

    'status.deviceKey': 'Record kept on this device',
    'status.noKey': 'No device key',
    'status.preparingKey': 'Preparing device key…',
    'status.corpusLive': 'Shared corpus · live',
    'status.corpusOffline': 'Shared corpus · offline',
    'status.corpusUnset': 'Corpus not configured',
    'status.sharing': 'Sharing readings',
    'status.notSharing': 'Not sharing',

    /* ── the plain-language opener ──────────────────────────────────── */
    'start.eyebrow': 'OmniAlera Ori',
    'start.title.line1': 'Your heartbeat,',
    'start.title.line2': 'read by light',
    'start.plain1': 'Your heart pushes blood through your face. Every push makes your skin the tiniest bit redder.',
    'start.plain2': 'Too small for you to see. But a camera can see it.',
    'start.plain3': 'So you look at a camera for a few seconds, and Ori counts your heartbeats.',
    'start.plain3b': 'No watch. No wires. Nothing to buy.',
    'start.problem': 'Here is the problem',
    'start.step1.lead': 'Skin has a colour.',
    'start.step1.body': 'Darker skin soaks up more of the light the camera is looking for, so the signal gets smaller.',
    'start.step2.lead': 'Most apps ignore this.',
    'start.step2.body': 'They show you a number anyway. It can be badly wrong while looking completely certain.',
    'start.step3.lead': 'Ori does not guess.',
    'start.step3.body': 'If it cannot see your heartbeat clearly, it tells you so. That is the whole difference.',
    'start.step4.lead': 'You can help fix it.',
    'start.step4.body': 'Every reading you choose to share — even a failed one — teaches the next version to see skin like yours. You are paid for it, and you can take it back any time.',
    'start.before.title': 'Before your first reading',
    'start.before.body': 'Pick the square closest to your own skin. Most health apps never ask. It is the one thing that decides whether the measurement will work at all.',
    'start.pledge.title': 'Take the Skin Pledge',
    'start.pledge.body': 'Let each reading — with nothing that identifies you — join the open dataset that teaches these models to see darker skin. You can watch the counter move, and switch this off whenever you like.',
    'start.pledge.earn': 'Earns 25 credits per reading',
    'start.cta.ready': 'Take my first reading',
    'start.cta.needTone': 'Choose a tone first',
    'start.cta.counter': 'See the counter first',
    'tone.lightest': 'Lightest',
    'tone.darkest': 'Darkest',
    'tone.chosen': 'Tone {n} of 10',
    'tone.none': 'Not chosen yet',
    'tone.label': 'Skin tone {n} of 10',
    'tone.group': 'Your skin tone',

    /* ── scan ───────────────────────────────────────────────────────── */
    'scan.eyebrow': 'Take a reading',
    'scan.title.idle': 'Hold still and look at the camera',
    'scan.title.running': 'Reading you now — hold still',
    'scan.title.locked': 'Got it',
    'scan.title.failed': 'Ori could not read you',
    'scan.intro':
      'Ori watches three parts of your face at once — your forehead and both cheeks — and reads each one two different ways. It only gives you a number when all of them agree.',
    'scan.start': 'Start',
    'scan.running': 'Reading…',
    'scan.again': 'Scan again',
    'scan.useCamera': 'Use my real camera',
    'scan.useSimulated': 'Back to the simulated subject',
    'scan.privacy':
      'Live camera, real time. Nothing you see leaves this device — the measurement runs in your browser, and no frame is ever uploaded.',
    'scan.simulated':
      'Simulated subject at skin tone {tone}, generated four times faster than real time so you are not left waiting. About {attenuation}% of the pulse signal survives at this tone.',
    'scan.skipped': '{count} frames skipped — Ori would rather wait than guess.',
    'scan.skipped.one': '1 frame skipped — Ori would rather wait than guess.',
    'scan.guess': 'Best guess so far: {bpm} bpm — not yet trusted.',
    'scan.guess.disagree': 'The two methods disagree, so Ori is still looking.',
    'scan.guess.settling': 'Waiting for it to settle.',

    'stat.elapsed': 'Elapsed',
    'stat.clarity': 'Clarity',
    'stat.agreement': 'Regions agree',
    'stat.pulse': 'Pulse',
    'stat.breathing': 'Breathing',
    'stat.variability': 'Variability',
    'unit.bpm': 'bpm',
    'unit.perMin': '/min',
    'unit.ms': 'ms',
    'unit.s': 's',

    /* ── live camera guidance ───────────────────────────────────────── */
    'advice.ok': 'Looking good — hold still.',
    'advice.no_face': 'No face found — centre yourself in the frame.',
    'advice.too_dark': 'Too dark. Face a window or a lamp.',
    'advice.dim': 'A little dark — more light will make this faster.',
    'advice.clipped': 'Overexposed — turn away from the light.',
    'advice.bright': 'Very bright. Try turning slightly away.',
    'advice.motion': 'Too much movement — hold still.',
    'advice.slight_motion': 'Try to keep still.',

    /* ── result ─────────────────────────────────────────────────────── */
    'card.share': 'Share this reading',
    'card.sharing': 'Preparing…',
    'card.downloaded': 'Saved to your device.',
    'card.failed': 'The card could not be made on this device.',
    'card.pulse': 'Pulse',
    'card.breathing': 'Breathing',
    'card.variability': 'Variability',
    'card.time': 'Time taken',
    'card.regions': 'Regions agreeing',
    'card.methods': 'Method spread',
    'card.tone': 'Skin tone',
    'card.trusted': 'Confirmed across three regions and two methods',
    'card.honest': 'Ori said so rather than guessing',
    'card.refusedLabel': 'No reading',
    'card.refusedHeadline': 'Ori could not see me clearly.',
    'card.lockedBody': 'Measured from light reflected off my skin — no watch, no cuff, nothing touching me. Ori only reports a number when independent regions and independent methods agree.',
    'card.refusedBody': 'Rather than print a confident wrong number, Ori refused — and recorded the refusal, so the failure can be measured and fixed instead of quietly repeating.',
    'card.disclaimer': 'Prototype. Not a medical device. Not a diagnosis.',

    'canvas.traceEmpty': 'the light coming back from your skin',
    'canvas.signalReaches': '{percent}% of the pulse signal reaches the sensor',
    'canvas.noReading': 'no reading yet',
    'canvas.steady': '{bpm} bpm · steady',
    'canvas.couldNotRead': 'could not read you',

    'result.title': 'Your reading',
    'result.found': 'found in {seconds} s',
    'result.none': 'no clear signal',
    'result.confirmed':
      'Confirmed across {regions} region readings and {methods} independent methods, agreeing to within {spread} bpm.',
    'result.refused':
      'Ori looked for the full window and never found a heartbeat it trusted. At this skin tone only about {attenuation}% of the pulse signal makes it back to the sensor. Rather than hand you a number it cannot stand behind, Ori is telling you it could not see you.',
    'result.conventional':
      'A conventional fixed-window estimator would have reported {bpm} bpm from the same data',
    'result.conventional.locked': '.',
    'result.conventional.failed': ', with no warning at all. That is what is shipping today.',

    /* ── diagnostics ────────────────────────────────────────────────── */
    'diag.toggle': 'What Ori is seeing',
    'diag.empty': 'Nothing yet. Ori needs about eight seconds of usable video before it has anything worth showing.',
    'diag.method': 'Method',
    'diag.reads': 'Reads',
    'diag.from': 'From',
    'diag.method.pos': 'Chrominance (POS)',
    'diag.method.green': 'Green channel',
    'diag.regions': '{count} regions',
    'diag.regions.one': '1 region',
    'diag.disagree':
      'The two methods differ by {spread} bpm. One of them is probably locked onto movement rather than a heartbeat, so Ori will not report either.',
    'diag.agree': 'Both methods agree to within {spread} bpm.',
    'diag.framesUsed': 'Frames used',
    'diag.harmonic': 'Harmonic support',
    'diag.spread': 'Spread across regions',
    'diag.perfusion': 'Perfusion',

    /* ── consent ────────────────────────────────────────────────────── */
    'consent.eyebrow': 'What you share',
    'consent.title.lead': 'Four switches.',
    'consent.title.em': 'All off until you say so.',
    'consent.lede': 'Each can be turned off again without losing your record, and each pays you while it is on.',
    'consent.offline.title': 'Not connected',
    'consent.offline.body': 'The Ori service is not reachable, so these switches cannot be changed right now. Your record is safe on this device, and nothing is being shared while you are offline.',
    'consent.error.title': 'That did not save',
    'consent.corpus.title': 'Help fix the model',
    'consent.corpus.body': 'Each reading, with nothing that identifies you, joins the open dataset that teaches these models to see darker skin. Failed readings are the most valuable kind of all.',
    'consent.corpus.earn': '25 credits per reading',
    'consent.routing.title': 'Suggest where to go next',
    'consent.routing.body': 'Let Ori point you at the right OmniAlera platform when a reading warrants it. Nothing is sent anywhere — the suggestion is worked out on your own device.',
    'consent.routing.earn': 'Free, and no data leaves',
    'consent.cohort.title': 'Join the research group',
    'consent.cohort.body': 'Be part of a de-identified long-term group that vetted research partners can study. Leave whenever you like: leaving removes you from every future extract, and partners holding past extracts are told.',
    'consent.cohort.earn': '10 extra credits per reading',
    'consent.assistant.title': 'Let Ori read my record',
    'consent.assistant.body': 'Allow a summary of your readings to travel with a question so Ori can answer about your own trends. Your identity is never part of it.',
    'consent.assistant.earn': 'Free',
    'consent.earned.title': 'What you have earned',
    'consent.earned.value': '{credits} credits',
    'consent.earned.body': 'Illustrative rates. In production these settle to mobile money. The point is that contributing is paid, not donated to a company that resells it.',
    'consent.withdraw.title': 'How a withdrawal actually works',
    'consent.withdraw.body1': 'Turning off the research group does three things, not one. You are removed from every extract built from now on. Any extract already built but not yet collected drops your rows before it is handed over. And for extracts already delivered, the partner holding them is issued a notice naming you as withdrawn.',
    'consent.withdraw.body2': 'That third one is the part almost nobody builds. Consent that cannot reach the data already out of the building is not consent — it is paperwork.',

    /* ── shared ─────────────────────────────────────────────────────── */
    'shared.why': 'Why this exists',
    'shared.kept': 'Kept on this device. Sharing is off.',
    'shared.keptNoService': 'Kept on this device — no corpus service is configured.',
    'shared.sending': 'Sharing…',
    'shared.added': 'Added to the corpus.',
    'shared.addedWithQueue':
      'Added to the corpus, along with {count} readings that had been waiting.',
    'shared.queuedOffline':
      'Saved on this device. It will be shared as soon as you are back online ({count} waiting).',
    'shared.refused': 'The service would not accept this reading, so nothing was shared.',
    'shared.flushed': '{count} readings that had been waiting are now in the corpus.',
    'shared.flushed.one': '1 reading that had been waiting is now in the corpus.',

    /* ── the two views ──────────────────────────────────────────────── */
    'view.group': 'Who this reading is for',
    'view.patient': 'For me',
    'view.clinician': 'For a clinician',

    /* ── the signal floor ───────────────────────────────────────────── */
    'floor.empty': 'Nothing to draw yet.',
    'floor.bar': 'what it has to clear',
    'floor.axis': 'possible rates',
    'floor.alt': 'Every rate Ori considered, and the level a candidate has to reach before Ori will report it.',
    'floor.caption.locked':
      'The tall mark is your heartbeat. It stands well clear of the dashed line, which is the level Ori requires before it will report anything.',
    'floor.caption.refused':
      'Nothing here reaches the dashed line. That line is the level Ori requires before it will report a rate — so it did not report one.',

    /* ── the clinician view ─────────────────────────────────────────── */
    'clin.locked': 'Locked at {bpm} bpm after {seconds} s. All five criteria satisfied.',
    'clin.refused': 'No reading. Failed on: {gates}.',
    'clin.refusedNoGate':
      'No reading. The window closed before any evaluation satisfied all five criteria.',
    'clin.region': 'Region',
    'clin.region.forehead': 'Forehead',
    'clin.region.leftCheek': 'Left cheek',
    'clin.region.rightCheek': 'Right cheek',
    'clin.region.other': 'Region {n}',
    'clin.attenuation': 'Signal reaching sensor',
    'clin.engine': 'Scanner',
    'clin.conventional':
      'A conventional fixed-window estimator would have reported {bpm} bpm from this same recording.',
    'clin.disclaimer':
      'Prototype. Not a medical device, not validated against a reference instrument, and not a basis for any clinical decision.',

    'gate.header.criterion': 'Criterion',
    'gate.header.measured': 'Measured',
    'gate.header.required': 'Required',
    'gate.prominence': 'Spectral prominence',
    'gate.harmonic': 'Harmonic support',
    'gate.stability': 'Temporal stability',
    'gate.agreement': 'Region agreement',
    'gate.methods': 'Method spread',
    'gate.pass': 'passed',
    'gate.fail': 'failed',
    'gate.skip': 'not assessed',
    'gate.notAssessed': 'not assessable — {regions} usable region(s)',


    /* ── what to expect at the tone you picked ──────────────────────── */
    'tone.expect.strong':
      'At this tone the signal is strong. Ori should be able to read you on most attempts.',
    'tone.expect.good':
      'At this tone the signal is good. Ori should usually be able to read you.',
    'tone.expect.weak':
      'At this tone the signal is weak. Ori will often not be able to read you — and when that happens it will say so, rather than show you a number it cannot stand behind.',
    'tone.expect.veryWeak':
      'At this tone the signal is very weak, and Ori will frequently not be able to read you at all. That is the honest limit of what a camera can see today. Every refusal you choose to share is what moves it.',

    'shared.notKept':
      'This reading could not be saved on your device. Nothing was shared, and the reading is gone — this is usually private browsing or a full storage quota.',

  },
};
